import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { GraduationCap, AlertCircle, CalendarX, FileText, TrendingDown, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, tone = 'gray', testId }) => {
  const toneCls = {
    gray: 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    sky: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  }[tone];
  return (
    <div className={`rounded-lg px-3 py-2 ${toneCls}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] opacity-80">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <p className="text-xl font-semibold mt-0.5">{value}</p>
    </div>
  );
};

const AttentionChip = ({ icon: Icon, label, value, tone = 'amber', testId }) => {
  if (!value) return null;
  const toneCls = {
    amber: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700',
    red: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700',
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium border px-2 py-1 rounded-full ${toneCls}`}
      data-testid={testId}
    >
      <Icon className="w-3 h-3" /> {label}: <strong>{value}</strong>
    </span>
  );
};

const AcademicOverviewCards = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/academic/overview`).then(r => { if (alive) setData(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const s = data.by_status || {};
  const att = data.attention || {};
  const anyAttention = (att.needs_attention || 0) + (att.at_risk || 0)
    + (att.recent_absent_or_no_show || 0) + (att.low_average_grade || 0)
    + (att.missing_attendance_this_week || 0) > 0;

  return (
    <div className="space-y-2" data-testid="academic-overview-wrap">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="academic-overview-cards">
        <StatCard icon={GraduationCap} label="Profiles" value={data.total_profiles ?? 0} testId="overview-profiles" />
        <StatCard icon={GraduationCap} label="Good" tone="emerald" value={s.Good ?? 0} testId="overview-good" />
        <StatCard icon={AlertCircle} label="Needs Attention" tone="amber" value={s['Needs Attention'] ?? 0} testId="overview-needs" />
        <StatCard icon={AlertCircle} label="At Risk" tone="red" value={s['At Risk'] ?? 0} testId="overview-risk" />
        <StatCard icon={GraduationCap} label="Graduated" tone="sky" value={s.Graduated ?? 0} testId="overview-grad" />
        <StatCard icon={CalendarX} label="Missing wk att." tone="amber" value={data.missing_attendance_this_week ?? 0} testId="overview-missing" />
        <StatCard icon={FileText} label="Recent reports" value={(data.recent_reports || []).length} testId="overview-recent-reports" />
      </div>
      {/* Phase 72.C — attention chip row (only renders when at least one signal fires). */}
      {anyAttention && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="academic-attention-row">
          <AttentionChip icon={AlertCircle} label="Needs Attention" value={att.needs_attention} tone="amber" testId="att-needs-attention" />
          <AttentionChip icon={AlertTriangle} label="At Risk" value={att.at_risk} tone="red" testId="att-at-risk" />
          <AttentionChip icon={CalendarX} label="Recent Absent / No-Show" value={att.recent_absent_or_no_show} tone="amber" testId="att-recent-absent" />
          <AttentionChip icon={TrendingDown} label="Low avg grade (< 50)" value={att.low_average_grade} tone="amber" testId="att-low-grade" />
          <AttentionChip icon={CalendarX} label="Missing this week" value={att.missing_attendance_this_week} tone="amber" testId="att-missing-week" />
        </div>
      )}
    </div>
  );
};

export default AcademicOverviewCards;
